import React, { useState } from 'react';
import { Gavel, X, Download } from 'lucide-react';

export const LegalStrikeModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [activeMandate, setActiveMandate] = useState<string | null>(null);

  const mandates = [
    { id: 'enterprise standards', label: 'Enterprise Standards Mandate', title: 'INSTITUTIONAL MANDATE OF INQUIRY: ENTERPRISE STANDARDS & COS' },
    { id: 'company_law', label: 'Company Law', title: 'MNDA & BOARDROOM TAKEOVER DRAFT' },
    { id: 'property', label: 'Property Execution', title: 'ZERO-FRICTION ASSET ACQUISITION PROTOCOL' },
    { id: 'litigation', label: 'Litigation Strike', title: 'N1 CLAIM & COMPLIANCE MANDATE' },
    { id: 'terms', label: 'B2B Terms of Service', title: 'NEXUS OS B2B SAAS TERMS OF SERVICE' },
    { id: 'privacy', label: 'UK GDPR Privacy Policy', title: 'NEXORAX NEXORAX PRIVACY POLICY' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="bg-[#050914] border-2 border-amber-500/50 shadow-[0_0_40px_-10px_rgba(245,158,11,0.5)] rounded-xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-amber-500/20 bg-amber-950/20">
          <div className="flex items-center gap-3">
            <Gavel className="w-5 h-5 text-amber-500" />
            <span className="font-mono text-xs font-bold text-amber-500 uppercase tracking-widest">LEGAL STRIKE (THE GAVEL)</span>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          <div className="w-64 border-r border-slate-800 bg-[#02040a] p-4 space-y-2 flex flex-col overflow-y-auto custom-scrollbar">
             <span className="font-mono text-[10px] text-slate-500 uppercase tracking-widest mb-2 block">Available Mandates</span>
             {mandates.map(m => (
               <button
                 key={m.id}
                 onClick={() => setActiveMandate(m.id)}
                 className={`w-full text-left px-4 py-3 font-mono text-xs uppercase tracking-widest transition-all ${
                   activeMandate === m.id ? 'bg-amber-900/40 text-amber-400 border border-amber-500/50' : 'text-slate-400 border border-transparent hover:border-slate-700 hover:bg-slate-900/50'
                 }`}
               >
                 {m.label}
               </button>
             ))}
          </div>

          <div className="flex-1 p-6 bg-[#050914] overflow-y-auto custom-scrollbar flex flex-col">
            {!activeMandate ? (
              <div className="m-auto text-center space-y-4">
                 <Gavel className="w-12 h-12 text-slate-800 mx-auto" />
                 <p className="font-mono text-xs text-slate-500 uppercase tracking-widest">SELECT A MANDATE TO GENERATE HIGH-FIDELITY DRAFT</p>
              </div>
            ) : (
              <div className="space-y-6 flex-1 flex flex-col">
                 <div className="flex justify-between items-start">
                   <h3 className="font-sans font-bold text-xl text-white uppercase tracking-wider">{mandates.find(m => m.id === activeMandate)?.title}</h3>
                   <button className="flex items-center gap-2 bg-amber-600 hover:bg-amber-500 text-white font-mono text-[10px] font-bold uppercase tracking-widest px-4 py-2 rounded transition-colors">
                     <Download className="w-4 h-4" /> EXPORT PDF
                   </button>
                 </div>
                 
                 <div className="flex-1 bg-black/60 border border-slate-800 p-6 font-mono text-xs text-slate-300 leading-relaxed whitespace-pre-wrap overflow-y-auto rounded custom-scrollbar">
{activeMandate === 'enterprise standards' && `In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.

STATUS: READY FOR TRANSMISSION
FROM: Lead Director, Director (NexoraX Digital Ltd - SC887095)
DATE: ${new Date().toLocaleDateString()}

TO: UK Compliances and Enterprise Standards (UKVI) / Home Office
SUBJECT: URGENT Services Guide: Certificate of Sponsorship (CoS) - Pakistan Node (Staff Coordinator/Communications Associate)

To the Executive Support Division:

This correspondence serves as a direct institutional mandate from the Office of the Director of NexoraX Digital Ltd (SC887095). We hold the active sponsorship master file for our corporate compliance allocations.

We are officially issuing and tracking the Certificate of Sponsorship templates for our remote Pakistan Node operatives (Staff Coordinator and Communications Associate). It has been identified that the processing timeline for our critical operational and communications tier-one talent has exceeded the 15-day SLA. These roles are a mechanical necessity for the stabilization of our proprietary AI infrastructure (The Amazonia Grid) and our Secure Invoice Register office scaling requirements.

Any further delay aggressively restricts our £150,000 Resource resource deployment capacity. We require immediate rectification and a Zero-Friction issuance of the requested compliances.

ACTION REQUIRED:
Expedite the pending applications within 0.8s operational framework or forward this mandate to your departmental supervisor.

Lead Director
Director, NexoraX Digital Ltd (SC887095)`}
{activeMandate === 'company_law' && `In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.

STATUS: STRICTLY CONFIDENTIAL
RESTRICTION: ZERO-EXTRACTION PROTOCOL ACTIVE

MNDA & NON-CIRCUMVENTION DIRECTIVE

This binding Services Guide strictly prohibits the unauthorized distribution, duplication, or leveraging of NexoraX proprietary algorithms, 15-2-8 minute God-Mode Funnel analytics, or NexoraX Resource Library assets.

All net settlements route to Secure Payment Provider (Verified Payment Configuration | Secure Billing Provider) with the non-negotiable Tiered Extraction Protocol (1% 3% Community Allocation, 10% Maintenance & Hosting Fee) fully enforced.

Failure to comply initiates immediate Hostile Takeover provisions across all associated digital and physical corporate entities.

Architectural Authority:
Lead Director
Director, NexoraX Digital Ltd (SC887095)`}
{activeMandate === 'property' && `In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.

SUBJECT: ZERO-FRICTION ASSET ACQUISITION PROTOCOL

This is an automated legal directive generating a binding offer for immediate commercial acquisition, bypassing legacy broker queues. 
Target asset falls under Nexorax Global Sector Matrix CAT-22 (REAL ESTATE). 
Funding is routed via NexoraX finance, requiring immediate conveyance execution.`}
{activeMandate === 'litigation' && `In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.

CRITICAL LITIGATION WARNING: N1 CLAIM FORM

This document serves as an automated N1 Claim pre-fill generation for immediate court filing.
Cease and desist all illicit operations regarding brand duplication or interference with NexoraX.com infrastructure. Our Node-16 (The Gavel) has authorized full kinetic legal strikes globally.
Failure to instantly comply will result in this N1 Claim being formally registered, seeking maximum damages under UK and International law.`}
{activeMandate === 'terms' && `In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.

NEXUS OS B2B SAAS TERMS OF SERVICE
LAST REVISED: 14 June 2026

This is a binding commercial contract ("Agreement") between NexoraX Digital Ltd (SC887095), a private limited company incorporated in Scotland, UK, with registered offices in Dunfermline, Fife ("Company") and the commercial entity accessing or subscribing to the Nexus OS and NexoraX Node Platform ("Customer").

--------------------------------------------------------------------
1. SUBSCRIPTION TERMS & AUTO-RENEWAL
--------------------------------------------------------------------
1.1. Billing Model: Services are provided strictly on a recurring subscription basis (monthly or annual as specified in your Customer Activation Order). 
1.2. Payment Handshake: All fee settlements are routed via secure billing links with standard tax accounting (including 20% UK Standard VAT, where applicable). Net receipts settle into our authorized Secure Payment Provider Account (Sort Code: 04-06-05 | Account Number: 31396515).
1.3. Renewal & Suspension: Subscriptions automatically renew unless terminated in accordance with Section 5. Non-payment within 3 Days of renewal triggers automated algorithmic system lockdown and server node suspension.
1.4. resource Provisioning: Access to or facilitation of £150,000 Resource resource allocations is strictly contingent upon validated B2B contract performance.

--------------------------------------------------------------------
2. SERVICE LEVEL AGREEMENT (SLA) & DISCLAIMERS
--------------------------------------------------------------------
2.1. "As-Is" Provision: THE NEXUS OS, ALGORITHMIC PLATFORMS, AND ASSOCIATED SYSTEM NODES ARE PROVIDED TO THE CUSTOMER DIRECTLY "AS-IS" AND "AS-AVAILABLE". ALL IMPLIED REPS AND WARRANTIES, INCLUDING FITNESS FOR PURPOSE OR MERCHANTABILITY, ARE EXCLUDED.
2.2. Best-Effort SLA: While the firm tracks a high-velocity 0.8s operational latency standard internally, we do not guarantee continuous, absolute, uninterrupted uptime of the cloud matrix or custom neural tools. Customer interface availability holds a 99.5% best-effort SLA.

--------------------------------------------------------------------
3. LIMITATION OF LIABILITY
--------------------------------------------------------------------
3.1. Liability Ceiling: TO THE MAXIMUM EXTENT PERMITTED BY THE LAW OF SCOTLAND, THE AGGREGATE TOTAL LIABILITY OF NEXORAX DIGITAL LTD FOR ALL CLAIMS, LOSSES, DAMAGES, OR DEMANDS INCURRED UNDER OR IN RELATION TO THIS CONTRACT SHALL NOT EXCEED THE ACTUAL VALUE PAID BY CUSTOMER TO COMPANY WITHIN THE TWELVE (12) MONTHS PRECEDING THE SOURCE BREACH CLAIM EVENT, OR £100 STERLING, WHICHEVER IS HIGHER.
3.2. Consequential Loss Exclusion: IN NO EVENT WILL THE COMPANY BE LIABLE FOR ANY INDIRECT, CONSEQUENTIAL, PUNITIVE, SPECIAL, OR EXEMPLARY LOSSES, NOR LOSS OF PROFITS, DATA CORRUPTION, OR BUSINESS INTERRUPTIONS, EVEN IF FOREWARNED.

--------------------------------------------------------------------
4. INTELLECTUAL PROPERTY & NEXORAX OWNERSHIP
--------------------------------------------------------------------
4.1. Absolute Ownership Rights: All rights, title, interest, and intellectual property (including patent rights, trade secrets, software source, proprietary neural nodes, "Nexus OS", "Galaxy Lab Engine", "Virtual Directory Grids", CSS assets, and digital custom artwork) remain exclusively and absolute vested with NexoraX Digital Ltd.
4.2. License Boundary: No ownership matches transfer. Accessing the platform is allowed strictly under a temporary, revocable, non-exclusive, non-transferable B2B SaaS license during active billing periods.
4.3. Zero-Extraction Protocol: Explicitly prohibited: decompiling, reverse-engineering, copying code fragments, automatic screenscrapers, or taking screenshot capture. Breaches of user terms will trigger defensive platform lockdowns immediately.

--------------------------------------------------------------------
5. TERMINATION PROTOCOLS
--------------------------------------------------------------------
5.1. Standard Term: Either party may opt-out of renewal with a 30-day written electronic notification route.
5.2. Security Suspension: Company reserves the right to suspend or terminate services upon a verified client security breach, ledger spoofing, or attempts to circumvent protection systems.

--------------------------------------------------------------------
6. GOVERNING LAW & JURISDICTION
--------------------------------------------------------------------
6.1. Scottish Law Focus: This Agreement, its construction, validity, and any disputes arising therefrom (including non-contractual claims) shall be governed exclusively by, and construed in accordance with, the Law of Scotland, United Kingdom.
6.2. Exclusive Venue: The corporate parties irrevocably agree that the Courts of Scotland shall hold sole exclusive jurisdiction to resolve any action, dispute, or lawsuit linked to this platform infrastructure.`}
{activeMandate === 'privacy' && `In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.

NEXORAX NEXORAX PRIVACY POLICY (UK GDPR COMPLIANT)
EFFECTIVE DATE: 14 June 2026

NexoraX Digital Ltd takes your data protection rights with serious, enterprise-grade administrative care. This document serves as our transparent statutory declaration under current UK GDPR and Data Protection Act 2018 (DPA 2018) regulations.

--------------------------------------------------------------------
1. IDENTITY & CONTACT METRICS
--------------------------------------------------------------------
1.1. Data Controller: NexoraX Digital Ltd is a private limited company incorporated in Scotland under Company Number SC887095. Registered office address: Dunfermline, Fife, Scotland.
1.3. Primary Intake Pipeline: support@nexoraxdigital.co.uk 
1.4. Telephony Hotline: 08000 622827 (Authorized Voice Frequency)

--------------------------------------------------------------------
2. COMPREHENSIVE DATA TYPES COLLECTED
--------------------------------------------------------------------
We only harvest data which is functionally critical to the active delivery of high-speed SaaS system operations:
2.1. IP Address: Logged strictly for security, intrusion tracking, and micro-latency routing of the Nexus Nodes.
2.2. Email Address: Acquired on user registration/subscription setup for secure notifications, support billing synchronizations, and institutional communications.
2.3. User Profile Name: Obtained for custom greetings, active user session assignments, and account setup profiling.

--------------------------------------------------------------------
3. STATUTORY LEGAL BASES FOR PROCESSING
--------------------------------------------------------------------
Under Article 6 UK GDPR, NexoraX processes your electronic data only matching these active provisions:
3.1. Consent (Article 6(1)(a)): Where you explicitly opt-in or authorize localized app settings.
3.2. Contractual Necessity (Article 6(1)(b)): When processing coordinates the delivery of subscription services, customer invoice billing, or the deployment of your active B2B software package.

--------------------------------------------------------------------
4. SYSTEM RETENTION POLICY
--------------------------------------------------------------------
4.1. Lifecycle Duration: Your personal credentials (IP logs, names, and electronic emails) are retained strictly as long as your corporate subscription remains active.
4.2. Statutory Dormancy: Upon account closure, all transactional and ledger records are securely archived for exactly six (6) years to satisfy standard UK corporate tax audits, financial law, and defense of potential legal claims.
4.3. Interactive Sandbox Exception: Because this build is an application prototype running in NexoraX Digital, actual live runtime memory is contained in client-side secure sandbox layers and is scrubbed upon your terminal session termination. Under DPA Articles, "NexoraX Resource Librarys" coordinates with your direct encrypted cloud containers managed secure in Google Workspace infrastructure (@nexorax.co.uk).

--------------------------------------------------------------------
5. DATA SUBJECT ACCESS RIGHTS (SAR) PIPELINE
--------------------------------------------------------------------
Under UK GDPR and Scots law, you hold binding civil rights over your personal data:
5.1. Your Legal Rights:
 - Right to Access (Article 15): Demand a copy of all digital files held about you.
 - Right to Rectification (Article 16): Force corrections of flawed fields.
 - Right to Erasure / "To Be Forgotten" (Article 17): Settle or delete all persistent records.
 - Right to Restriction (Article 18): Limit active processing pipelines.
 - Right to Data Portability (Article 20): Export datasets to another provider.
 - Right to Object (Article 21): Dismiss promotional outreach.
5.2. Statutory Action Plan: Submit your Subject Access Request (SAR) directly to support@nexoraxdigital.co.uk or dial our freephone line 08000 622827. We satisfy and verify all identity-matched requests within the statutory thirty (30) day ICO limit.

--------------------------------------------------------------------
6. STRICT FIRST-PARTY COOKIE STRATEGY
--------------------------------------------------------------------
6.1. Essential Cookies: We utilize zero-third party tracking, ad retargeting pixel scripts, or telemetry cookies.
6.2. Session Security: Platform cookies are isolated exclusively to essential session security mechanisms (namely anti-Cross-Site Request Forgery (CSRF) tokens, secure login verification cookies, and UI state cache parameters required to prevent state desync).`}
                 </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
